import React, { useCallback, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, View } from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import AttendanceHeader from "./AttendanceHeader";
import Text from "@components/CustomText";
import { getAttendanceRoles } from "@services/api";
import { ATTENDANCE_ROLES, roleTileSubtitle } from "@utils/attendance";
import { ATTENDANCE_COLORS as C } from "./constants";
import PhoneModal from "./PhoneModal";
import RoleTile from "./RoleTile";
import type {
  AttendanceDetailParams,
  AttendanceRole,
  IdentifyResponse,
  RoleEntry,
  RolesResponse,
} from "./types";

interface NavigationLike {
  navigate: (name: string, params?: AttendanceDetailParams) => void;
  goBack: () => void;
}

interface Props {
  navigation: NavigationLike;
  route: { name: string };
}

const DEFAULT_ROLES: { role: AttendanceRole; title: string; badge: string }[] =
  [
    {
      role: ATTENDANCE_ROLES.EXECUTIVE,
      title: "Mark Executive Attendance",
      badge: "EX",
    },
    {
      role: ATTENDANCE_ROLES.JR_EXECUTIVE,
      title: "Mark Jr. Executive Attendance",
      badge: "JR",
    },
  ];

const MarkAttendance = ({ navigation, route }: Props) => {
  const [entries, setEntries] = useState<RoleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeRole, setActiveRole] = useState<AttendanceRole | null>(null);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      const load = async () => {
        try {
          const res = await getAttendanceRoles();
          if (cancelled) return;
          if (res?.status === 200 && Array.isArray(res.data?.roles)) {
            setEntries((res.data as RolesResponse).roles);
            setLoadError(null);
          } else {
            setLoadError(
              res?.data?.message || "Could not load attendance status.",
            );
          }
        } catch (_e) {
          if (!cancelled) setLoadError("Could not load attendance status.");
        } finally {
          if (!cancelled) setLoading(false);
        }
      };
      load();
      return () => {
        cancelled = true;
      };
    }, []),
  );

  const handleIdentified = useCallback(
    (identity: IdentifyResponse, phone: string) => {
      setActiveRole(null);
      navigation.navigate("AttendanceDetail", {
        identity,
        phone,
        issuedAt: Date.now(),
      });
    },
    [navigation],
  );

  const closeModal = useCallback(() => setActiveRole(null), []);

  return (
    <View style={styles.container}>
      <AttendanceHeader
        headertitle="Mark Attendance"
        navigation={navigation}
        route={route}
      />
      <ScrollView contentContainerStyle={styles.content}>
        <Text style={styles.hint}>
          Select the role you are marking attendance for.
        </Text>
        {loading ? (
          <ActivityIndicator style={styles.loader} color={C.pending} />
        ) : null}
        {loadError ? <Text style={styles.error}>{loadError}</Text> : null}
        {DEFAULT_ROLES.map((def) => {
          const entry = entries.find((e) => e.role === def.role);
          return (
            <RoleTile
              key={def.role}
              testID={`attendance-role-${def.role}`}
              badge={entry?.badge ?? def.badge}
              title={entry?.title ?? def.title}
              subtitle={roleTileSubtitle(entry)}
              marked={false}
              onPress={() => setActiveRole(def.role)}
            />
          );
        })}
      </ScrollView>
      <PhoneModal
        visible={activeRole !== null}
        role={activeRole}
        onClose={closeModal}
        onIdentified={handleIdentified}
      />
    </View>
  );
};

export default MarkAttendance;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.white },
  content: { padding: 16, gap: 14 },
  hint: { color: C.muted, fontSize: 13 },
  loader: { marginVertical: 8 },
  error: { color: C.pending, fontSize: 13 },
});
